import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wand2, CheckCircle2, XCircle, Trash2, Loader2, AlertTriangle, PlayCircle } from "lucide-react";

const HH = Array.from({ length: 24 }, (_, i) => i);
const MM = [0, 15, 30, 45];

export default function FAStagingPanel({ reloadKey = 0 }) {
  const [data, setData] = useState({ rows: [], pitches: [], teams: [], counts: { pending: 0, approved: 0, conflicts: 0, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/fa-staging");
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to load staging"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const pitchName = (id) => (data.pitches.find(p => p.id === id) || {}).name || "—";
  const teamName = (id) => (data.teams.find(t => t.id === id) || {}).name || "";
  const grouped = useMemo(() => {
    const g = {};
    data.rows.forEach(r => { (g[r.booking_date] = g[r.booking_date] || []).push(r); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data.rows]);

  const selectableIds = useMemo(() => data.rows.map(r => r.id), [data.rows]);
  const allChecked = selected.size > 0 && selected.size === selectableIds.length;
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(selectableIds));

  const patch = async (id, body) => {
    try {
      const r = await api.put(`/admin/fa-staging/${id}`, body);
      setData(prev => ({ ...prev, rows: prev.rows.map(row => row.id === id ? r.data : row) }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Update failed"); }
  };

  const autoSchedule = async () => {
    setBusyAction("auto");
    try {
      const r = await api.post("/admin/fa-staging/auto-schedule");
      toast.success(`Auto-scheduled ${r.data.updated}${r.data.unresolved ? ` · ${r.data.unresolved} unresolved` : ""}`);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Auto-schedule failed"); }
    finally { setBusyAction(""); }
  };

  const bulkAction = async (endpoint, verb, filterConflict = false) => {
    const ids = [...selected].filter(id => {
      const row = data.rows.find(r => r.id === id);
      if (!row) return false;
      if (filterConflict && row.conflict_reason) return false;
      return true;
    });
    if (ids.length === 0) return toast.error(`Nothing to ${verb}`);
    setBusyAction(verb);
    try {
      const r = await api.post(endpoint, { ids });
      toast.success(`${verb}: ${r.data.approved ?? r.data.reverted ?? r.data.created ?? ids.length}`);
      setSelected(new Set());
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || `${verb} failed`); }
    finally { setBusyAction(""); }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Remove this fixture from staging?")) return;
    try { await api.delete(`/admin/fa-staging/${id}`); await load(); setSelected(prev => { const n = new Set(prev); n.delete(id); return n; }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed"); }
  };

  const clearAll = async () => {
    if (!window.confirm(`Remove all ${data.counts.total} staging rows?`)) return;
    try { await api.post("/admin/fa-staging/clear-all"); await load(); setSelected(new Set()); toast.success("Staging cleared"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Clear failed"); }
  };

  if (loading && data.rows.length === 0) {
    return <div className="card-tactical rounded-md p-6 text-center opacity-70"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading staging…</div>;
  }

  return (
    <div className="card-tactical rounded-md p-5 space-y-4" data-testid="fa-staging-panel">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <div className="font-display font-bold text-2xl tracking-tight">Fixture staging pool</div>
          <div className="text-xs opacity-70 mt-1">
            {data.counts.total} staged · <span className="text-[#83B68B]">{data.counts.approved} approved</span> · {data.counts.pending} pending
            {data.counts.conflicts > 0 && <> · <span className="text-red-400">{data.counts.conflicts} conflict(s)</span></>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={autoSchedule} disabled={busyAction === "auto" || data.counts.pending === 0} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="fa-staging-auto">
            {busyAction === "auto" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Auto-schedule pending
          </button>
          {data.counts.total > 0 && (
            <button onClick={clearAll} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-red-400 hover:text-red-500 rounded inline-flex items-center gap-1.5" data-testid="fa-staging-clear-all">
              <Trash2 className="w-3 h-3" /> Clear all
            </button>
          )}
        </div>
      </div>

      {data.rows.length === 0 ? (
        <div className="text-sm opacity-60 italic py-6 text-center">Nothing staged yet. Send fixtures from the FA preview above.</div>
      ) : (
        <>
          <div className="text-[10px] tracking-widest uppercase opacity-50 border-t border-border pt-3 -mb-1">
            Auto-schedule uses fair-share: teams who haven't opened the day recently get priority for the earliest slot.
          </div>
          <div className="flex gap-2 flex-wrap border-t border-border pt-4">
            <label className="flex items-center gap-2 text-xs tracking-widest uppercase opacity-70 select-none cursor-pointer">
              <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="fa-staging-toggle-all" />
              Select all ({selected.size})
            </label>
            <div className="flex-1" />
            <button onClick={() => bulkAction("/admin/fa-staging/approve", "Approved", true)} disabled={selected.size === 0 || busyAction !== ""} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B]/10 rounded inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="fa-staging-approve">
              <CheckCircle2 className="w-3 h-3" /> Approve proposal ({selected.size})
            </button>
            <button onClick={() => bulkAction("/admin/fa-staging/unapprove", "Reverted")} disabled={selected.size === 0 || busyAction !== ""} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-amber-400 hover:text-amber-500 rounded inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="fa-staging-unapprove">
              <XCircle className="w-3 h-3" /> Unapprove
            </button>
            <button onClick={() => bulkAction("/admin/fa-staging/confirm", "Booked")} disabled={selected.size === 0 || busyAction !== ""} className="btn-volt px-4 py-1.5 rounded inline-flex items-center gap-1.5 text-xs tracking-widest uppercase disabled:opacity-40" data-testid="fa-staging-confirm">
              <PlayCircle className="w-3 h-3" /> Confirm as bookings
            </button>
          </div>

          <div className="space-y-6">
            {grouped.map(([date, rows]) => (
              <div key={date}>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] mb-2 border-b border-border pb-1">
                  {format(parseISO(date), "EEEE d MMMM yyyy")} · {rows.length}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left opacity-60">
                      <tr>
                        <th className="p-2 w-8"></th>
                        <th className="p-2">Status</th>
                        <th className="p-2">Home team</th>
                        <th className="p-2">Opponent</th>
                        <th className="p-2">Kick-off</th>
                        <th className="p-2">Pitch</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => (
                        <tr key={r.id} className={`border-t border-border ${r.conflict_reason ? "bg-red-500/5" : ""}`} data-testid={`fa-staging-row-${r.id}`}>
                          <td className="p-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`fa-staging-check-${r.id}`} /></td>
                          <td className="p-2 whitespace-nowrap">
                            {r.proposal_status === "approved"
                              ? <span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-[#83B68B]/20 text-[#83B68B]">Approved</span>
                              : <span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-white/10 opacity-70">Pending</span>}
                            {r.conflict_reason && (
                              <div className="text-[10px] text-red-400 mt-1 flex items-center gap-1" title={r.conflict_reason}>
                                <AlertTriangle className="w-3 h-3" /> Conflict
                              </div>
                            )}
                          </td>
                          <td className="p-2">
                            <div className="font-medium">{r.home_team_name || teamName(r.team_id) || "—"}</div>
                            {r.age_group && <div className="text-[10px] opacity-60">{r.age_group}{r.game_format ? ` · ${r.game_format}` : ""}{r.competition ? ` · ${r.competition}` : ""}</div>}
                          </td>
                          <td className="p-2 opacity-80">{r.opponent || "—"}</td>
                          <td className="p-2 whitespace-nowrap">
                            <div className="flex gap-1 items-center">
                              <Select value={String(r.start_hour)} onValueChange={v => patch(r.id, { start_hour: Number(v) })}>
                                <SelectTrigger className="w-16 h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{HH.map(h => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}</SelectItem>)}</SelectContent>
                              </Select>
                              <span className="opacity-40">:</span>
                              <Select value={String(r.start_min || 0)} onValueChange={v => patch(r.id, { start_min: Number(v) })}>
                                <SelectTrigger className="w-14 h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{MM.map(m => <SelectItem key={m} value={String(m)}>{String(m).padStart(2, "0")}</SelectItem>)}</SelectContent>
                              </Select>
                              <span className="opacity-40 mx-1">→</span>
                              <Select value={String(r.end_hour)} onValueChange={v => patch(r.id, { end_hour: Number(v) })}>
                                <SelectTrigger className="w-16 h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{HH.slice(1).map(h => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}</SelectItem>)}</SelectContent>
                              </Select>
                              <span className="opacity-40">:</span>
                              <Select value={String(r.end_min || 0)} onValueChange={v => patch(r.id, { end_min: Number(v) })}>
                                <SelectTrigger className="w-14 h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{MM.map(m => <SelectItem key={m} value={String(m)}>{String(m).padStart(2, "0")}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          </td>
                          <td className="p-2">
                            <Select value={r.pitch_id || ""} onValueChange={v => patch(r.id, { pitch_id: v || null })}>
                              <SelectTrigger className="w-44 h-7 text-xs"><SelectValue placeholder="Pick pitch" /></SelectTrigger>
                              <SelectContent>
                                {data.pitches.map(p => (
                                  <SelectItem key={p.id} value={p.id}>{p.name}{p.game_format ? ` (${p.game_format})` : ""}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <button onClick={() => removeRow(r.id)} className="text-red-400 hover:text-red-500" data-testid={`fa-staging-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
